import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Target, TrendingUp, Globe2, Home, ShieldCheck, Users, Zap, PoundSterling } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Internal-only "Vision vs Reality" tracker.
 *
 * Public surfaces (TrustStrip, hero, footer) MUST only render numbers
 * derived live from /api/stats — never any of the targets below.
 * This component is the ONLY place vision/aspirational numbers may appear.
 */
const VISION_TARGETS = {
  countries: 5,            // UK + IN + CA + AU + US
  harvested_properties: 3467,
  verified_users: 100,
  users: 1000,
  instant_matches: 500,    // AI Instant Match emails sent (buyer onboarding)
};

// Revenue projection assumptions — kept here (internal) so finance & growth
// can tune without redeploying. Conversion rate is conservative.
const REV_ASSUMPTIONS = {
  conversion_rate: 0.10,     // 10% of harvested listings unlocked
  unlock_fee_gbp: 15,        // £15 per unlock (matches instant_match email copy)
};

const Row = ({ icon, label, target, actual, unit }) => {
  const pct = target > 0 ? Math.min(100, Math.round((Number(actual || 0) / target) * 100)) : 0;
  const ok = pct >= 100;
  return (
    <div className="border border-slate-200 rounded p-4" data-testid={`vision-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-700">
          {icon}
          <span className="text-xs uppercase tracking-wider font-bold">{label}</span>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${
          ok
            ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50'
            : 'bg-amber-950/95 text-amber-300 border-amber-500/50'
        }`}>
          {ok ? 'On target' : `${pct}% of vision`}
        </span>
      </div>
      <div className="flex items-baseline gap-3 mt-3">
        <span className="text-3xl font-bold text-[#0F172A] font-['Playfair_Display']">{actual ?? '—'}</span>
        <span className="text-xs text-slate-500">/ {target}{unit ? ` ${unit}` : ''} target</span>
      </div>
      <div className="w-full bg-slate-100 h-2 rounded mt-3 overflow-hidden">
        <div className="h-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const AdminVisionTargets = () => {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/stats`)
      .then(r => { if (!cancelled) setStats(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.message || 'Failed to load stats'); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-vision-targets">
      <div>
        <h2 className="font-['Playfair_Display'] text-3xl text-[#0F172A] mb-1 flex items-center gap-2">
          <Target className="h-6 w-6 text-[#D4AF37]" /> Vision vs Reality
        </h2>
        <p className="text-sm text-slate-600">
          Internal tracker. <span className="font-bold text-red-600">Never displayed publicly</span> — only the live numbers from <code className="text-xs bg-slate-100 px-1 rounded">/api/stats</code> appear on the homepage. Update <code className="text-xs bg-slate-100 px-1 rounded">VISION_TARGETS</code> in <code className="text-xs bg-slate-100 px-1 rounded">AdminVisionTargets.js</code> to revise the goals.
        </p>
      </div>

      {err && (
        <div className="border border-red-300 bg-red-50 text-red-700 text-sm p-3 rounded">
          Failed to load live stats: {err}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Row
          icon={<Globe2 className="h-4 w-4" />}
          label="Active countries"
          target={VISION_TARGETS.countries}
          actual={stats?.countries}
        />
        <Row
          icon={<Home className="h-4 w-4" />}
          label="AI-matched homes"
          target={VISION_TARGETS.harvested_properties}
          actual={stats?.harvested_properties}
        />
        <Row
          icon={<ShieldCheck className="h-4 w-4" />}
          label="Verified Partners"
          target={VISION_TARGETS.verified_users}
          actual={stats?.verified_users}
        />
        <Row
          icon={<Users className="h-4 w-4" />}
          label="Total users"
          target={VISION_TARGETS.users}
          actual={stats?.users}
        />
        <Row
          icon={<Zap className="h-4 w-4" />}
          label="AI Instant Matches"
          target={VISION_TARGETS.instant_matches}
          actual={stats?.instant_matches}
        />
      </div>

      {/* Projected revenue — derived from live harvested-listings count.
          Conservative 10% conversion × £15 unlock fee. No extra API call. */}
      {stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-emerald-200 bg-emerald-50/60 rounded p-4" data-testid="projected-revenue-card">
            <div className="flex items-center gap-2 text-emerald-800 mb-2">
              <PoundSterling className="h-4 w-4" />
              <span className="text-xs uppercase tracking-wider font-bold">Projected unlock revenue</span>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-[#0F172A] font-['Playfair_Display']" data-testid="projected-revenue-value">
                £{Math.round((stats.harvested_properties || 0) * REV_ASSUMPTIONS.conversion_rate * REV_ASSUMPTIONS.unlock_fee_gbp).toLocaleString('en-GB')}
              </span>
              <span className="text-xs text-emerald-700">
                {Math.round(REV_ASSUMPTIONS.conversion_rate * 100)}% of {stats.harvested_properties || 0} listings × £{REV_ASSUMPTIONS.unlock_fee_gbp}
              </span>
            </div>
            <p className="text-[11px] text-emerald-700/80 mt-2">
              Internal forecast — assumes conservative 10% conversion of AI Real Estate Network listings at the standard £15 unlock fee.
            </p>
          </div>
          <div className="border border-slate-200 bg-slate-50/60 rounded p-4" data-testid="matches-generated-card">
            <div className="flex items-center gap-2 text-slate-700 mb-2">
              <Zap className="h-4 w-4 text-[#D4AF37]" />
              <span className="text-xs uppercase tracking-wider font-bold">Total matches generated</span>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-[#0F172A] font-['Playfair_Display']" data-testid="matches-generated-value">
                {(stats.instant_matches || 0).toLocaleString('en-GB')}
              </span>
              <span className="text-xs text-slate-500">buyer match emails sent</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Source: <code className="text-[10px] bg-slate-100 px-1 rounded">db.instant_match_log.count()</code> — fired on every buyer registration.
            </p>
          </div>
        </div>
      ) : null}

      {stats?.active_countries?.length ? (
        <div className="border border-slate-200 rounded p-4">
          <div className="flex items-center gap-2 text-slate-700 mb-2">
            <TrendingUp className="h-4 w-4" />
            <span className="text-xs uppercase tracking-wider font-bold">Live country list (rendered publicly)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.active_countries.map(c => (
              <span key={c} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border bg-[#0F172A] text-yellow-300 border-yellow-500/60">
                {c}
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Source of truth: <code className="text-xs bg-slate-100 px-1 rounded">db.properties.distinct(&quot;country&quot;)</code> ∪ <code className="text-xs bg-slate-100 px-1 rounded">db.harvested_listings.distinct(&quot;country&quot;)</code> with <em>active</em> filters and empty-string strip.
          </p>
        </div>
      ) : null}

      <div className="border border-amber-300 bg-amber-50 text-amber-800 text-xs p-3 rounded">
        <strong>Compliance note:</strong> the public TrustStrip and any marketing copy must always pull from <code>/api/stats</code> — no hardcoded numbers. This guards against ASA / FTC / DGCC misleading-claim risk if reality drops below the vision.
      </div>
    </div>
  );
};

export default AdminVisionTargets;
